<template>
  <v-container class="car-number-detail" fluid>
    <div class="car-number-detail__header">
      <h1 class="car-number-detail__title">Подробности по гос. номеру</h1>
    </div>

    <v-progress-linear
      v-if="loading"
      indeterminate
      color="primary"
      class="mb-4"
    />

    <template v-else-if="error">
      <v-alert type="error" class="mt-4">{{ error }}</v-alert>
    </template>

    <template v-else-if="result">
      <!-- Сводка -->
      <v-card class="car-number-detail__summary mb-4" outlined>
        <v-card-text class="pa-4">
          <div class="summary-main">
            <div class="summary-number-block">
              <span class="summary-number-label">Гос. номер</span>
              <span class="summary-number-value">{{ result.summary.car_number_original || result.summary.car_number_normalized }}</span>
            </div>
            <div class="summary-stats">
              <v-chip small class="stat-chip" outlined>
                Записей: {{ result.summary.total_in_car_numbers }}
              </v-chip>
              <v-chip small class="stat-chip" outlined>
                История гостей: {{ result.summary.total_in_guest_history }}
              </v-chip>
              <v-chip small class="stat-chip" outlined>
                Гост. пропуска: {{ result.summary.total_in_guest_passes }}
              </v-chip>
              <v-chip small class="stat-chip" outlined>
                Абоненты: {{ result.summary.total_in_abonents }}
              </v-chip>
            </div>
          </div>
        </v-card-text>
      </v-card>

      <!-- Записи car_numbers: клиент, шлагбаум -->
      <v-card v-if="result.car_numbers && result.car_numbers.length" class="mb-4" outlined>
        <v-card-title class="subtitle-1 car-number-detail__section-title">
          <v-icon left>mdi-format-list-bulleted</v-icon>
          Привязки номера (клиент и шлагбаум)
        </v-card-title>
        <v-data-table
          :headers="carNumberHeaders"
          :items="result.car_numbers"
          :items-per-page="5"
          :disable-sort="true"
          class="elevation-0"
        >
          <template v-slot:item.client_name="{ item }">
            {{ item.client_name || '—' }}
          </template>
          <template v-slot:item.client_telephone="{ item }">
            {{ formatPhone(item.client_telephone) }}
          </template>
          <template v-slot:item.barrier="{ item }">
            <template v-if="item.is_portfolio">
              <v-chip x-small color="grey lighten-2">Портфолио</v-chip>
            </template>
            <template v-else>
              <router-link v-if="item.barrier_id" :to="`/barrier/${item.barrier_id}`" class="car-number-detail__link">
                {{ formatBarrierLabel(item) }}
              </router-link>
              <span v-else>—</span>
            </template>
          </template>
          <template v-slot:item.is_active="{ item }">
            <v-chip x-small :color="item.is_active ? 'success' : 'grey'" text-color="white">
              {{ item.is_active ? 'Активен' : 'Неактивен' }}
            </v-chip>
          </template>
          <template v-slot:item.last_changed_at="{ item }">
            {{ formatDateTime(item.last_changed_at) }}
          </template>
          <template v-slot:item.comment="{ item }">
            {{ item.comment || '—' }}
          </template>
          <template v-slot:item.created_at="{ item }">
            {{ formatDateTime(item.created_at) }}
          </template>
          <template v-slot:item.actions="{ item }">
            <div class="text-right">
              <v-btn v-if="item.client_id" small text color="primary" :to="`/client/${item.client_id}`">
                Карточка клиента
              </v-btn>
            </div>
          </template>
        </v-data-table>
      </v-card>

      <!-- История гостевого доступа -->
      <v-card v-if="result.guest_access_history && result.guest_access_history.length" class="mb-4" outlined>
        <v-card-title class="subtitle-1 car-number-detail__section-title">
          <v-icon left>mdi-history</v-icon>
          История гостевого доступа
        </v-card-title>
        <v-data-table
          :headers="guestHistoryHeaders"
          :items="result.guest_access_history"
          :items-per-page="5"
          :disable-sort="true"
          class="elevation-0"
        >
          <template v-slot:item.client_name="{ item }">
            <router-link v-if="item.client_id" :to="`/client/${item.client_id}`" class="car-number-detail__link">
              {{ item.client_name || item.client_id || '—' }}
            </router-link>
            <span v-else>{{ item.client_name || item.client_id || '—' }}</span>
          </template>
          <template v-slot:item.barrier="{ item }">
              <router-link v-if="item.barrier_id" :to="`/barrier/${item.barrier_id}`" class="car-number-detail__link">
              {{ formatBarrierLabel(item) }}
            </router-link>
            <span v-else>—</span>
          </template>
          <template v-slot:item.period="{ item }">
            {{ formatDateRange(item.date_begin, item.date_end) }}
          </template>
          <template v-slot:item.comment="{ item }">
            {{ item.comment || '—' }}
          </template>
          <template v-slot:item.created_at="{ item }">
            {{ formatDateTime(item.created_at) }}
          </template>
        </v-data-table>
      </v-card>

      <!-- Гостевые пропуска -->
      <v-card v-if="result.guest_passes && result.guest_passes.length" class="mb-4" outlined>
        <v-card-title class="subtitle-1 car-number-detail__section-title">
          <v-icon left>mdi-ticket-account</v-icon>
          Гостевые пропуска
        </v-card-title>
        <v-data-table
          :headers="guestPassesHeaders"
          :items="result.guest_passes"
          :items-per-page="5"
          :disable-sort="true"
          class="elevation-0"
        >
          <template v-slot:item.name="{ item }">
            {{ item.name || '—' }}
          </template>
          <template v-slot:item.telephone="{ item }">
            {{ formatPhone(item.telephone) }}
          </template>
          <template v-slot:item.barrier_ids="{ item }">
            <template v-if="item.barrier_ids && item.barrier_ids.length">
              <template v-if="item.barrier_ids.length >= 3">
                <v-btn
                  small
                  text
                  class="text-none px-0 car-number-detail__barriers-more-btn"
                  @click="openGuestPassBarriersModal(item.barrier_ids)"
                >
                  {{ formatGuestPassBarriersCompact(item.barrier_ids) }}
                </v-btn>
              </template>
              <template v-else>
                <span v-for="(barrierId, idx) in item.barrier_ids" :key="`guest-pass-barrier-${item.id}-${barrierId}-${idx}`">
                  <router-link :to="`/barrier/${barrierId}`" class="car-number-detail__link">
                    {{ formatGuestPassBarrierLabel(barrierId) }}
                  </router-link><span v-if="idx < item.barrier_ids.length - 1">, </span>
                </span>
              </template>
            </template>
            <span v-else>—</span>
          </template>
          <template v-slot:item.client_name="{ item }">
            <router-link v-if="item.client_id" :to="`/client/${item.client_id}`" class="car-number-detail__link">
              {{ item.client_name || '—' }}
            </router-link>
            <span v-else>—</span>
          </template>
          <template v-slot:item.period="{ item }">
            {{ formatDateRange(item.date_begin, item.date_end) }}
          </template>
          <template v-slot:item.comment="{ item }">
            {{ item.comment || '—' }}
          </template>
          <template v-slot:item.created_at="{ item }">
            {{ formatDateTime(item.created_at) }}
          </template>
        </v-data-table>
      </v-card>

      <!-- Абоненты с этим номером -->
      <v-card v-if="result.abonents && result.abonents.length" class="mb-4" outlined>
        <v-card-title class="subtitle-1 car-number-detail__section-title">
          <v-icon left>mdi-account-multiple</v-icon>
          Абоненты с этим номером
        </v-card-title>
        <v-data-table
          :headers="abonentsHeaders"
          :items="result.abonents"
          :items-per-page="5"
          :disable-sort="true"
          class="elevation-0"
        >
          <template v-slot:item.address="{ item }">
            {{ formatAbonentAddress(item) }}
          </template>
          <template v-slot:item.name="{ item }">
            {{ item.name || '—' }}
          </template>
          <template v-slot:item.telephone="{ item }">
            {{ formatPhone(item.telephone) }}
          </template>
          <template v-slot:item.clients="{ item }">
            <span v-for="(c, idx) in (item.clients || [])" :key="c.client_id">
              <router-link :to="`/client/${c.client_id}`" class="car-number-detail__link">
                {{ c.client_name }}
              </router-link><span v-if="idx < (item.clients || []).length - 1">, </span>
            </span>
            <span v-if="!(item.clients && item.clients.length)">—</span>
          </template>
          <template v-slot:item.created_at="{ item }">
            {{ formatDateTime(item.created_at) }}
          </template>
        </v-data-table>
      </v-card>

      <!-- Журналы проездов: загружаются по запросу -->
      <v-card class="mb-4" outlined>
        <v-card-title class="subtitle-1 car-number-detail__section-title">
          <v-icon left>mdi-clipboard-list-outline</v-icon>
          Где и когда проезжал — журналы
        </v-card-title>
        <v-card-text>
          <v-progress-linear v-if="journalLoading" indeterminate color="primary" class="mb-3" />
          <template v-else-if="journalEntries.length">
            <v-data-table
              :headers="journalHeaders"
              :items="journalEntries"
              :items-per-page="5"
              :disable-sort="true"
              class="elevation-0"
            >
              <template v-slot:item.date="{ item }">
                {{ formatJournalDate(item.date) }}
              </template>
              <template v-slot:item.barrier="{ item }">
                <router-link v-if="item.barrier_id" :to="`/barrier/${item.barrier_id}`" class="car-number-detail__link">
                  {{ formatBarrierAddressOnly(item) }}
                </router-link>
                <span v-else>—</span>
              </template>
              <template v-slot:item.car_number="{ item }">
                {{ item.car_number || '—' }}
              </template>
              <template v-slot:item.result="{ item }">
                {{ item.opening_info === '-\r' ? '—' : 'Успешно' }}
              </template>
            </v-data-table>
          </template>
          <template v-else-if="result && journalLoaded">
            <p class="body-2 grey--text mb-0 car-number-detail__empty-note">За последние 30 дней записей не найдено.</p>
          </template>
          <template v-else-if="result && uniqueBarriersFromCarNumbers.length === 0">
            <p class="body-2 grey--text mb-0 car-number-detail__empty-note">Нет привязок к шлагбаумам — журнал проездов недоступен.</p>
          </template>
        </v-card-text>
      </v-card>
    </template>

    <v-dialog v-model="guestPassBarriersDialog" max-width="560">
      <v-card>
        <v-card-title class="subtitle-1 car-number-detail__section-title">
          <v-icon left>mdi-barrier</v-icon>
          Шлагбаумы гостевого пропуска
        </v-card-title>
        <v-card-text class="pt-4">
          <div v-if="guestPassBarriersDialogItems.length">
            <div
              v-for="(entry, idx) in guestPassBarriersDialogItems"
              :key="`guest-pass-modal-barrier-${entry.id}-${idx}`"
              class="mb-2"
            >
              <router-link :to="`/barrier/${entry.id}`" class="car-number-detail__link" @click.native="guestPassBarriersDialog = false">
                {{ entry.label }}
              </router-link>
            </div>
          </div>
          <div v-else class="grey--text">
            Нет данных по шлагбаумам.
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text color="primary" @click="guestPassBarriersDialog = false">
            Закрыть
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import ClientApi from '@/services/api/client';
import BarrierApi from '@/services/api/barrier';

export default {
  name: 'CarNumberSearchDetail',
  data () {
    return {
      carNumberHeaders: [
        { text: 'Клиент', value: 'client_name' },
        { text: 'Телефон', value: 'client_telephone' },
        { text: 'Шлагбаум', value: 'barrier' },
        { text: 'Статус', value: 'is_active', width: '120' },
        { text: 'Изменено', value: 'last_changed_at', width: '170' },
        { text: 'Комментарий', value: 'comment' },
        { text: 'Создано', value: 'created_at', width: '170' },
        { text: 'Действия', value: 'actions', sortable: false, align: 'right', width: '170' },
      ],
      guestHistoryHeaders: [
        { text: 'Клиент', value: 'client_name' },
        { text: 'Шлагбаум', value: 'barrier' },
        { text: 'Период', value: 'period' },
        { text: 'Комментарий', value: 'comment' },
        { text: 'Создано', value: 'created_at', width: '170' },
      ],
      guestPassesHeaders: [
        { text: 'Имя гостя', value: 'name' },
        { text: 'Телефон', value: 'telephone' },
        { text: 'Шлагбаумы', value: 'barrier_ids' },
        { text: 'Клиент', value: 'client_name' },
        { text: 'Период', value: 'period' },
        { text: 'Комментарий', value: 'comment' },
        { text: 'Создано', value: 'created_at', width: '170' },
      ],
      abonentsHeaders: [
        { text: 'Адрес', value: 'address' },
        { text: 'Имя', value: 'name' },
        { text: 'Телефон', value: 'telephone' },
        { text: 'Клиенты', value: 'clients' },
        { text: 'Создано', value: 'created_at', width: '170' },
      ],
      journalHeaders: [
        { text: 'Дата и время', value: 'date' },
        { text: 'Шлагбаум', value: 'barrier' },
        { text: 'Номер', value: 'car_number' },
        { text: 'Результат', value: 'result', width: '130' },
      ],
      loading: false,
      error: '',
      result: null,
      journalLoading: false,
      journalEntries: [],
      journalLoaded: false,
      barrierAddressById: {},
      guestPassBarriersDialog: false,
      guestPassBarriersDialogItems: [],
    };
  },
  computed: {
    carNumber () {
      return (this.$route.query.car_number || '').trim();
    },
    carNumberNormalized () {
      return (this.result?.summary?.car_number_normalized || this.carNumber || '').replace(/\s/g, '');
    },
    uniqueBarriersFromCarNumbers () {
      if (!this.result?.car_numbers) return [];
      const seen = new Set();
      return this.result.car_numbers.filter(row => {
        if (!row.barrier_id || seen.has(row.barrier_id)) return false;
        seen.add(row.barrier_id);
        return true;
      });
    },
    uniqueClientIds () {
      if (!this.result) return [];
      const ids = new Set();
      (this.result.car_numbers || []).forEach(r => { if (r.client_id) ids.add(r.client_id); });
      (this.result.guest_access_history || []).forEach(r => { if (r.client_id) ids.add(r.client_id); });
      (this.result.guest_passes || []).forEach(r => { if (r.client_id) ids.add(r.client_id); });
      (this.result.abonents || []).forEach(a => {
        (a.clients || []).forEach(c => { if (c.client_id) ids.add(c.client_id); });
      });
      return Array.from(ids);
    },
  },
  watch: {
    $route: {
      immediate: true,
      handler (to) {
        const num = (to.query.car_number || '').trim();
        if (to.name === 'CarNumberSearchDetail' && num) {
          this.load();
        } else if (to.name === 'CarNumberSearchDetail' && !num) {
          this.result = null;
          this.error = 'Не указан гос. номер для поиска.';
          this.journalEntries = [];
          this.journalLoaded = false;
        }
      },
    },
  },
  methods: {
    load () {
      if (!this.carNumber) {
        this.result = null;
        this.error = 'Не указан гос. номер для поиска.';
        this.journalEntries = [];
        this.journalLoaded = false;
        return;
      }
      this.loading = true;
      this.error = '';
      this.result = null;
      this.journalEntries = [];
      this.journalLoaded = false;
      ClientApi.getCarNumberSearch(this.carNumber)
        .then((response) => {
          const data = response.data;
          if (data && data.status && data.summary !== undefined) {
            this.result = data;
            this.loadBarrierAddresses();
            this.loadJournals();
          } else {
            this.result = null;
            this.error = 'По гос. номеру ничего не найдено.';
            this.journalEntries = [];
            this.journalLoaded = true;
          }
        })
        .catch((err) => {
          this.result = null;
          this.error = err?.response?.data?.message || 'Ошибка при загрузке данных по гос. номеру.';
          this.journalEntries = [];
          this.journalLoaded = true;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    async loadBarrierAddresses () {
      const allBarrierIds = new Set();
      (this.result?.guest_passes || []).forEach((pass) => {
        (pass.barrier_ids || []).forEach((id) => {
          if (id != null && id !== '') allBarrierIds.add(Number(id));
        });
      });
      if (!allBarrierIds.size) {
        this.barrierAddressById = {};
        return;
      }
      try {
        const response = await BarrierApi.loadAllBarriers({ limit: 1000 });
        const payload = response?.data;
        const list = Array.isArray(payload)
          ? payload
          : (Array.isArray(payload?.data) ? payload.data : []);
        const map = {};
        list.forEach((barrier) => {
          const id = Number(barrier?.id);
          if (!id || !allBarrierIds.has(id)) return;
          map[id] = (barrier?.address || barrier?.address_info?.address || barrier?.addressInfo?.address || barrier?.barrier_address || '')
            .toString()
            .trim();
        });
        this.barrierAddressById = map;
      } catch (err) {
        this.barrierAddressById = {};
      }
    },
    async loadJournals () {
      const barriers = this.uniqueBarriersFromCarNumbers;
      if (!barriers.length || !this.carNumberNormalized) {
        this.journalLoaded = true;
        return;
      }
      this.journalLoading = true;
      this.journalEntries = [];
      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - 30);
      const startStr = start.toISOString().slice(0, 10) + ' 00:00:00';
      const endStr = end.toISOString().slice(0, 10) + ' 23:59:59';
      const carNumbers = [this.carNumberNormalized];
      try {
        const responses = await Promise.all(
          barriers.map(row =>
            BarrierApi.getJournalGrzOpen(row.barrier_id, startStr, endStr, null, carNumbers),
          ),
        );
        const barrierById = {};
        barriers.forEach(row => { barrierById[row.barrier_id] = row.barrier_address || row.barrier_name || ''; });
        const entries = [];
        responses.forEach((response, i) => {
          const list = response.data?.data ?? response.data ?? [];
          const barrierId = barriers[i].barrier_id;
          const barrierAddress = barrierById[barrierId] || '';
          (Array.isArray(list) ? list : []).forEach(item => {
            entries.push({
              ...item,
              barrier_id: barrierId,
              barrier_address: barrierAddress,
            });
          });
        });
        entries.sort((a, b) => (b.date || '').localeCompare(a.date || ''));
        this.journalEntries = entries;
      } catch (err) {
        console.error(err);
      } finally {
        this.journalLoading = false;
        this.journalLoaded = true;
      }
    },
    formatJournalDate (dateStr) {
      if (!dateStr) return '—';
      const s = String(dateStr).replace(/([-+]\d+)$/, '');
      if (!s) return dateStr;
      const d = new Date(s);
      if (isNaN(d.getTime())) return dateStr;
      return d.toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'medium' });
    },
    formatPhone (phone) {
      if (!phone) return '—';
      const digits = String(phone).replace(/\D/g, '');
      if (digits.length >= 11) {
        return `+${digits.slice(0, 1)} (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7, 9)}-${digits.slice(9, 11)}`;
      }
      return phone;
    },
    formatDateTime (value) {
      if (!value) return '—';
      const raw = String(value).trim().replace(' ', 'T');
      const d = new Date(raw);
      if (isNaN(d.getTime())) return value;
      return d.toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
    formatDateOnly (value) {
      if (!value) return '—';
      const raw = String(value).trim().replace(' ', 'T');
      const d = new Date(raw);
      if (isNaN(d.getTime())) return value;
      return d.toLocaleDateString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    },
    formatDateRange (begin, end) {
      if (!begin && !end) return '—';
      const start = this.formatDateOnly(begin);
      const finish = this.formatDateOnly(end);
      if (start !== '—' && finish !== '—') return `с ${start} по ${finish}`;
      return start !== '—' ? `с ${start}` : `по ${finish}`;
    },
    formatBarrierLabel (row) {
      if (!row) return '—';
      const name = (row.barrier_name || '').trim();
      const rawAddress = (row.barrier_address || '').trim();
      const address = this.trimCityFromAddress(rawAddress);
      if (name && address) return `${name} (${address})`;
      if (address) return address;
      if (rawAddress) return rawAddress;
      if (name) return name;
      return row.barrier_id ? `Шлагбаум №${row.barrier_id}` : '—';
    },
    formatBarrierAddressOnly (row) {
      if (!row) return '—';
      const address = this.formatAddressWithoutCity(row.barrier_address || '');
      if (address !== '—') return address;
      return row.barrier_id ? `Шлагбаум №${row.barrier_id}` : '—';
    },
    trimCityFromAddress (address) {
      if (!address) return '';
      const source = String(address).trim();
      const normalized = source
        .replace('г.Ульяновск', '')
        .replace(/^,\s*/, '')
        .trim();
      return normalized || source;
    },
    formatAddressWithoutCity (address) {
      if (!address) return '—';
      const normalized = this.trimCityFromAddress(address);
      return normalized || String(address);
    },
    formatAbonentAddress (abonent) {
      if (!abonent) return '—';
      const address = this.formatAddressWithoutCity(abonent.address);
      const apartment = abonent.apartment ? `кв.${abonent.apartment}` : '';
      return [address !== '—' ? address : '', apartment].filter(Boolean).join(', ') || '—';
    },
    formatGuestPassBarrierLabel (barrierId) {
      const address = this.barrierAddressById[Number(barrierId)] || '';
      if (!address) return `№${barrierId}`;
      const normalized = this.trimCityFromAddress(address);
      return normalized || `№${barrierId}`;
    },
    formatGuestPassBarriersCompact (barrierIds = []) {
      if (!barrierIds.length) return '—';
      const firstLabel = this.formatGuestPassBarrierLabel(barrierIds[0]);
      return `${firstLabel} ...`;
    },
    openGuestPassBarriersModal (barrierIds = []) {
      this.guestPassBarriersDialogItems = (barrierIds || []).map((id) => ({
        id,
        label: this.formatGuestPassBarrierLabel(id),
      }));
      this.guestPassBarriersDialog = true;
    },
  },
};
</script>

<style lang="scss" scoped>
.car-number-detail__header {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  padding: 14px 18px;
  border-radius: 16px;
  background: linear-gradient(135deg, #eef3f8 0%, #e7eef6 55%, #dde7f2 100%);
  border: 1px solid rgba(93, 114, 137, 0.18);
}

.car-number-detail__title {
  font-size: 1.45rem;
  font-weight: 600;
  margin: 0;
  color: #3f5268;
  letter-spacing: 0.02em;
  text-align: center;
}

.car-number-detail__summary {
  border-radius: 16px;
  .summary-main {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
  }
  .summary-number-block {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
  .summary-number-label {
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: rgba(0, 0, 0, 0.5);
  }
  .summary-number-value {
    font-size: 1.35rem;
    font-weight: 600;
    letter-spacing: 0.08em;
    color: #263238;
  }
  .summary-stats {
    display: flex;
    flex-wrap: wrap;
    gap: 8px 12px;
  }
  .stat-chip {
    font-size: 0.78rem;
    border-color: rgba(0, 0, 0, 0.18);
    color: #455a64;
  }
}

.car-number-detail .v-card {
  border-radius: 16px;
}

.car-number-detail__section-title {
  background: #f7f8fa;
  color: #37474f;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  font-weight: 500;
}

.car-number-detail__empty-note {
  text-align: center;
}

.v-data-table td,
.v-data-table th {
  padding: 8px 16px;
}

.car-number-detail__link {
  color: inherit;
  text-decoration: none;
}

.car-number-detail__link:hover {
  text-decoration: underline;
}

.car-number-detail__barriers-more-btn {
  min-width: 0 !important;
}
</style>
